// React wiring for the CinematicDirector: feeds it `milestone:reached`
// events from the sim event stream (sim.events, fed by adapter.subscribe),
// game time, the settings and the pause claim, and exposes a test hook:
//
//   window.__aod.testMilestone({ milestoneId: 'city.tier_up', data: { tier: 2 } }, { uiMode: 'icons' })
//   window.__aod.cinematicState()   → { active: recipeId | null, pending }
//   window.__aod.cameraPose()       → the live camera pose (audits compare before/after)
//
// Missing payload fields are filled in (local player, tier, current tick,
// the local capital as position), so audits and manual checks can trigger
// a moment with one call. Like window.__aodTest, the hook is installed in
// every build: cinematics are client-only presentation, so triggering one
// can't affect the game.

import {
  type GameSnapshot,
  MILESTONE_EVENT_TYPE,
  type MilestonePayload,
  isMilestonePayload,
} from '@aod/protocol';
import { useEffect, useMemo, useRef, useSyncExternalStore } from 'react';
import { getSoundManager } from '../audio/useSounds.js';
import { scalePoint } from '../render/boardScale.js';
import { findCapitalFocus } from '../render/capitalFocus.js';
import { useSettings } from '../settings/useSettings.js';
import { getCinematicCamera } from './cameraBridge.js';
import {
  CinematicDirector,
  type DirectorMode,
  type DirectorState,
  type OfferOptions,
  type OfferResult,
  type PauseHandle,
  type WorldFraming,
} from './director.js';
import type { CameraPose, UiMode, Vec3 } from './types.js';

export interface UseCinematicDirectorOptions {
  readonly mode: DirectorMode;
  /** Only process events while this view is the one on screen. */
  readonly enabled: boolean;
  readonly events: ReadonlyArray<{ readonly type: string; readonly payload?: unknown }>;
  readonly tick: number;
  readonly snapshot: GameSnapshot | null;
  readonly localPlayer: number;
  /** The seat's ui_mode, when the match setup provides one. */
  readonly uiMode?: UiMode;
  /** Single-player: the pause controller's cinematic claim. */
  readonly pause?: PauseHandle | null;
}

export interface CinematicDirectorHandle {
  readonly director: CinematicDirector;
  readonly state: DirectorState;
}

interface AodWindowHooks {
  testMilestone?: (
    payload: Partial<MilestonePayload>,
    opts?: OfferOptions,
  ) => OfferResult | 'invalid';
  cinematicState?: () => { active: string | null; pending: number };
  cameraPose?: () => CameraPose | null;
}

/** Local player's capital and the bounding circle of their cities. */
export function worldFraming(snapshot: GameSnapshot | null, localPlayer: number): WorldFraming {
  if (!snapshot) return { capital: null, empire: null };
  const capital = findCapitalFocus(snapshot, localPlayer);
  const own: Vec3[] = [];
  for (const n of snapshot.scene.children ?? []) {
    if (n.type !== 'city') continue;
    if ((n.props as { ownerId?: unknown } | undefined)?.ownerId !== localPlayer) continue;
    const p = n.transform.position;
    own.push([p[0], p[1], p[2]]);
  }
  if (own.length === 0) return { capital, empire: null };
  const cx = own.reduce((s, p) => s + p[0], 0) / own.length;
  const cz = own.reduce((s, p) => s + p[2], 0) / own.length;
  const radius = Math.max(...own.map((p) => Math.hypot(p[0] - cx, p[2] - cz))) + 3;
  return { capital, empire: { center: [cx, 0, cz], radius } };
}

export function fillTestMilestone(
  partial: Partial<MilestonePayload>,
  localPlayer: number,
  tick: number,
  world: WorldFraming,
): MilestonePayload {
  const milestoneId = partial.milestoneId ?? 'city.tier_up';
  const tier = partial.tier ?? (milestoneId === 'victory.achieved' ? 'finale' : 'big');
  const position = partial.position ?? world.capital ?? undefined;
  return {
    playerIndex: localPlayer,
    tick,
    ...partial,
    milestoneId,
    tier,
    ...(position ? { position } : {}),
    ...(milestoneId === 'city.tier_up' && !partial.data ? { data: { tier: 2 } } : {}),
  } as MilestonePayload;
}

function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  } catch {
    return false;
  }
}

export function useCinematicDirector(opts: UseCinematicDirectorOptions): CinematicDirectorHandle {
  const { mode, enabled, events, tick, snapshot, localPlayer } = opts;
  const setting = useSettings((s) => s.cinematics);

  // Values the director reads lazily, always current.
  const live = useRef({ tick, snapshot, localPlayer, pause: opts.pause ?? null });
  live.current = { tick, snapshot, localPlayer, pause: opts.pause ?? null };

  const director = useMemo(
    () =>
      new CinematicDirector({
        getCamera: getCinematicCamera,
        getPause: () => live.current.pause,
        getWorld: () => worldFraming(live.current.snapshot, live.current.localPlayer),
        prefersReducedMotion,
        playSound: (id) => {
          const sm = getSoundManager();
          if (sm.isInitialized) sm.play(id);
        },
        now: () => performance.now(),
      }),
    [],
  );

  director.configure({ mode, setting, localPlayer, uiMode: opts.uiMode ?? 'standard' });

  // New milestone events → offer(). Events accumulate for the whole match;
  // a shorter array means a new match, so start over.
  const processed = useRef(0);
  useEffect(() => {
    if (events.length < processed.current) {
      processed.current = 0;
      director.reset();
    }
    const fresh = events.slice(processed.current);
    processed.current = events.length;
    if (!enabled) return;
    for (const e of fresh) {
      if (e.type === MILESTONE_EVENT_TYPE && isMilestonePayload(e.payload)) {
        director.offer(e.payload, live.current.tick);
      }
    }
  }, [events, enabled, director]);

  useEffect(() => {
    director.update(tick);
  }, [tick, director]);

  // End a running cinematic cleanly when the view goes away.
  useEffect(() => () => director.skip(), [director]);

  useEffect(() => {
    if (!enabled || typeof window === 'undefined') return;
    const w = window as unknown as { __aod?: AodWindowHooks };
    const hooks: AodWindowHooks = {
      testMilestone: (partial, offerOpts) => {
        const { tick: now, snapshot: snap, localPlayer: me } = live.current;
        const p = fillTestMilestone(partial ?? {}, me, now, worldFraming(snap, me));
        if (!isMilestonePayload(p)) return 'invalid';
        return director.offer(p, now, offerOpts);
      },
      cinematicState: () => ({
        active: director.getState().active?.recipeId ?? null,
        pending: director.getPending().length,
      }),
      // Exposed in rendered world space (board scale applied) so audit math
      // against __aodCamera.world stays consistent; getPose itself is
      // board-space for the setActive restore round-trip.
      cameraPose: () => {
        const p = getCinematicCamera()?.getPose();
        if (!p) return null;
        return {
          position: scalePoint(p.position),
          target: scalePoint(p.target),
        };
      },
    };
    w.__aod = { ...w.__aod, ...hooks };
    return () => {
      const current = w.__aod;
      if (current && current.testMilestone === hooks.testMilestone) {
        const { testMilestone: _a, cinematicState: _b, cameraPose: _c, ...rest } = current;
        w.__aod = rest;
      }
    };
  }, [enabled, director]);

  const state = useSyncExternalStore(director.subscribe, director.getState, director.getState);
  return { director, state };
}
